import get from "lodash/get";
import { useCallback, useMemo, useState } from "react";

import { adminStatsEndpoints, type AdminStatsQueryParams, type ReferrerHierarchyStats } from "../../../api";
import { useStrapiList } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import usePagination from "../../hooks/usePagination";
import handleError from "../../utils/handleError";

interface UseReferrersHierarchyStatsProps {
  pagination?: { page: number; pageSize: number };
  params?: AdminStatsQueryParams;
  autoLoad?: boolean;
  [key: string]: unknown;
}

const useReferrersHierarchyStats = ({ pagination, params, ...props }: UseReferrersHierarchyStatsProps = {}) => {
  const {
    items: referrersStats,
    isLoading,
    filters,
    setFilters,
    changeFilters,
  } = useStrapiList<ReferrerHierarchyStats>({
    ...props,
    load: () => loadReferrersHierarchyStats(params),
    onLoadError: (e: unknown) => handleError(getErrorMessage(e, "Error while loading ReferrersHierarchyStats")),
  });

  // Ensure referrersStats is always an array
  const referrersStatsArray = useMemo(() => {
    if (!referrersStats) return [];
    return Array.isArray(referrersStats) ? referrersStats : [];
  }, [referrersStats]);

  // Filtering.
  //
  // These filters are applied client-side against ReferrerHierarchyStats rows,
  // whose identity fields live under `referrer`. Reading them from the row root
  // yielded undefined, and comparing a stringified value against a raw boolean
  // never matched, so ticking VIP or typing in the search box emptied the
  // table instead of narrowing it.
  const filteredReferrersStats = useMemo(() => {
    if (!referrersStatsArray?.length || !filters) return referrersStatsArray;

    const FIELD_PATHS: Record<string, string> = {
      isVIP: "referrer.isVIP",
      referrerType: "referrer.referrerType",
      blocked: "referrer.blocked",
    };

    let copy = [...referrersStatsArray];
    for (const [field, filter] of Object.entries(filters)) {
      if (filter === undefined || filter === "") continue;

      if (field === "search") {
        const needle = String(filter).trim().toLowerCase();
        if (!needle) continue;
        copy = copy.filter((item) => {
          const name = String(get(item, "referrer.name") ?? "").toLowerCase();
          const email = String(get(item, "referrer.email") ?? "").toLowerCase();
          return name.includes(needle) || email.includes(needle);
        });
        continue;
      }

      const path = FIELD_PATHS[field] ?? field;
      copy = copy.filter((item) => {
        const v = get(item, path);
        if (Array.isArray(filter)) return filter.includes(v);
        if (filter === null) return v == null;
        // `isVIP` and friends are nullish booleans, so a missing value must
        // read as false rather than excluding the row outright.
        if (typeof filter === "boolean") return !!v === filter;
        return String(v) === String(filter);
      });
    }
    return copy;
  }, [referrersStatsArray, filters]);

  // Sorting
  const [sort, setSort] = useState<Record<string, string>>({ "charged.EUR.total": "down" });
  const changeSort = useCallback(
    (field: string, direction: string | null) => setSort(direction ? { [field]: direction } : {}),
    [setSort]
  );
  const sortedReferrersStats = useMemo(() => {
    const [field, direction] = Object.entries(sort)?.[0] || [];
    if (!filteredReferrersStats?.length || !field || !direction) return filteredReferrersStats;
    const copy = [...filteredReferrersStats];
    copy.sort((itemA, itemB) => {
      const valA = get(itemA, field, 0);
      const valB = get(itemB, field, 0);
      return direction === "down" ? valB - valA : valA - valB;
    });
    return copy;
  }, [filteredReferrersStats, sort]);

  const { pageItems: pageReferralsStats, ...paginationProps } = usePagination(sortedReferrersStats, pagination);

  return {
    rawReferrersHierarchyStats: referrersStatsArray,
    referrersHierarchyStats: pageReferralsStats,
    isLoading,
    sort,
    changeSort,
    ...paginationProps,
    filters,
    setFilters,
    changeFilters,
  };
};

export const loadReferrersHierarchyStats = async (params?: AdminStatsQueryParams) => {
  try {
    // Return the full { data, meta } response - useStrapiList handles this format
    return await adminStatsEndpoints.getReferrersHierarchyStats(params);
  } catch (e: unknown) {
    handleError(getErrorMessage(e, "Error while loading ReferrersHierarchyStats"));
  }
};

export default useReferrersHierarchyStats;
